import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  api,
  ApiError,
  getAccessToken,
  getApiBase,
  isStaticMode,
  type FeedState,
  type FeedbackAction,
  type PostItem,
} from '@/api/client'

export const useFeedStore = defineStore('feed', () => {
  const feed = ref<FeedState | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const toast = ref<string | null>(null)
  let toastTimer: number | undefined

  function showToast(msg: string) {
    toast.value = msg
    window.clearTimeout(toastTimer)
    toastTimer = window.setTimeout(() => {
      toast.value = null
    }, 2200)
  }

  /** True when writes cannot hit a live authenticated API. */
  function readOnly(): boolean {
    const snap = !!(feed.value as any)?.static_mode
    const browse = !!(feed.value as any)?.static_browse
    // pure static host, no apiBase
    if ((snap || isStaticMode() || browse) && !getApiBase()) return true
    // hybrid configured but offline snapshot only
    if (getApiBase() && (browse || (snap && !getAccessToken()))) return true
    // hybrid live path still needs token to write
    if (getApiBase() && !getAccessToken()) return true
    return false
  }

  function readOnlyReason(): string {
    if (getApiBase() && !getAccessToken()) return '請先登入存取碼才能批閱'
    if ((feed.value as any)?.static_browse) return '目前離線快照：連上 Tailscale 後再批'
    return '雲端只讀：請連 Tailscale 並登入後再批'
  }

  async function load(params: { days?: number; mode?: string; cat?: string } = {}) {
    loading.value = true
    error.value = null
    try {
      feed.value = await api.feed({
        days: params.days ?? 2,
        mode: params.mode ?? 'throne',
        cat: params.cat ?? 'all',
      })
    } catch (e: any) {
      error.value = e?.message || String(e)
    } finally {
      loading.value = false
    }
  }

  async function feedback(postId: string, action: FeedbackAction, reading_ms?: number) {
    if (readOnly()) {
      showToast(readOnlyReason())
      throw new ApiError('readonly', readOnlyReason())
    }
    // Must succeed on server first — never fake then revert
    const r = await api.feedback(postId, action, reading_ms)
    if (feed.value) {
      const drop = (arr: PostItem[]) => arr.filter((x) => x.id !== postId)
      feed.value.deck_items = drop(feed.value.deck_items || [])
      feed.value.inbox = drop(feed.value.inbox || [])
      feed.value.view_items = drop(feed.value.view_items || [])
      if (feed.value.hub?.inbox?.buckets) {
        const b = feed.value.hub.inbox.buckets
        for (const k of Object.keys(b)) b[k] = drop(b[k] || [])
      }
      if (feed.value.hub?.inbox?.tags) {
        for (const k of Object.keys(feed.value.hub.inbox.tags)) {
          feed.value.hub.inbox.tags[k] = drop(feed.value.hub.inbox.tags[k] || [])
        }
      }
      if (feed.value.counts) {
        feed.value.counts.deck = feed.value.deck_items.length
        feed.value.counts.inbox = feed.value.inbox.length
        feed.value.counts.interest = feed.value.hub?.inbox?.buckets?.interest?.length || 0
        feed.value.counts.news = feed.value.hub?.inbox?.buckets?.news?.length || 0
        feed.value.counts.other = feed.value.hub?.inbox?.buckets?.other?.length || 0
      }
      if (action === 'like' || action === 'save' || action === 'skip') {
        feed.value.throne_pending = Math.max(0, (feed.value.throne_pending || 0) - 1)
      }
    }
    const sec =
      reading_ms != null && reading_ms > 0 ? ` · 讀 ${Math.round(reading_ms / 100) / 10}s` : ''
    const labels: Record<string, string> = {
      like: '有興趣' + sec,
      skip: '這篇普通' + sec,
      save: '已存' + sec,
      topic: '有興趣',
      dislike: '這篇普通',
      hide: '這篇普通',
    }
    showToast(labels[r.action] || '已記錄')
    return r
  }

  return { feed, loading, error, toast, showToast, load, feedback, readOnly, readOnlyReason }
})
